import React, { useState } from 'react';
import {
	Box,
	Typography,
	Divider,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Collapse,
	Button,
	CircularProgress,
	useMediaQuery,
	useTheme,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LaptopIcon from '@mui/icons-material/Laptop';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useSelector } from 'react-redux';
import { useOnboarding } from 'contextProviders/OnboardingProvider';
import { TourChecklistItem } from 'api/onboarding/types';
import { selectEnabledApps } from 'redux/enabledApps/enabledAppsSlice';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { GATED_APPS, AppId } from 'constants/apps';
import { useTourDriver, MOBILE_SAFE_TOUR_KEY } from './tour/useTourDriver';
import { isTourRegistered } from './tour/tourRegistry';
import { useTicketingTourReady } from './tour/useTicketingTourReady';

interface OnboardingChecklistProps {
	/** Called after a tour is launched (e.g. to close the popup). */
	onStartTour?: () => void;
}

// These tours render at the top level, ungrouped (app-wide, not tied to a
// product area). Everything else collapses under a per-app header.
const ROOT_TOUR_KEYS = new Set(['canapps_navigation']);

// CANpark tours that need at least one parking lot to run (record a plate,
// monitor a site, register a plate) — gated in the checklist until a lot exists.
const LOT_REQUIRED_TOURS = new Set([
	'canpark_issue_ticket',
	'canpark_monitor_site',
	'canpark_register_plate',
]);

// Explicit display order within an app group (the backend returns tours roughly
// by name, which doesn't match the intended learning sequence). Tours listed here
// sort first in this order; anything not listed keeps its backend order after
// them (Array.sort is stable).
const TOUR_ORDER: Record<string, number> = {
	canpark_manage_sites: 0,
	canpark_issue_ticket: 1,
	canpark_monitor_site: 2,
	canpark_register_plate: 3,
};
const tourRank = (tour: TourChecklistItem): number =>
	TOUR_ORDER[tour.tourKey] ?? Number.POSITIVE_INFINITY;

const statusMeta = (status: TourChecklistItem['status']) => {
	switch (status) {
		case 'completed':
			return {
				label: 'Completed',
				icon: <CheckCircleIcon color="success" fontSize="small" />,
			};
		case 'started':
			return {
				label: 'In progress',
				icon: <PendingOutlinedIcon color="warning" fontSize="small" />,
			};
		case 'skipped':
			return {
				label: 'Skipped',
				icon: <RadioButtonUncheckedIcon color="disabled" fontSize="small" />,
			};
		default:
			return {
				label: 'Not started',
				icon: <RadioButtonUncheckedIcon color="disabled" fontSize="small" />,
			};
	}
};

// Popup content listing the user's walkthroughs with their progress. The
// app-wide navigation tour sits at the root; product-area tours (CANmeet,
// CANpark, …) collapse under a per-app header.
const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
	onStartTour,
}) => {
	const { tours, loading, error } = useOnboarding();
	const { enabledApps, isLoaded: appsLoaded } = useSelector(selectEnabledApps);
	// selectUserInfo returns the slice state; the user data is nested under it.
	const userData = useSelector(selectUserInfo)?.userInfo;
	const { startTour } = useTourDriver();
	const theme = useTheme();
	// Below 'md' the nav/sidebar most tours anchor to is behind the hamburger, so
	// only the mobile-safe record flow can run — matches the gate in useTourDriver.
	const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
	const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
	// The tour awaiting confirmation. Clicking Start/Replay stages the tour here
	// (a confirm dialog opens); nothing actually starts — no navigation, no popup
	// close — until the user confirms. `confirmOpen` is kept separate from the tour
	// data so the data stays mounted through the close animation (clearing it
	// immediately would shrink the dialog's text mid-transition).
	const [pendingTour, setPendingTour] = useState<TourChecklistItem | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);

	// Everything we can match a role_checklist entry against, lower-cased: the
	// user's legacy role names (e.g. "CanPark Admin"), their role-group names
	// (e.g. "CANpark_Admin"), and their V3 role keys. Being forgiving here lets an
	// admin seed role_checklist with whichever identifier they see.
	const userRoleNames = new Set<string>(
		[
			...(userData?.roleNames ?? []),
			...(userData?.userRoleGroups ?? []).map(
				(g: { name: string }) => g.name,
			),
			...(userData?.roles ?? []),
		].map((r) => r.toLowerCase()),
	);

	// Only surface tours for apps the instance is subscribed to. Umbrella apps
	// (e.g. CANapps) aren't gated, so their tours always show. Gated apps
	// (CANpark, CANmeet, …) require an explicit `enabled` entitlement — and we
	// fail closed: hide them until the enablement map has loaded.
	const isAppAllowed = (tour: TourChecklistItem): boolean => {
		const appId = tour.app.toLowerCase() as AppId;
		if (!GATED_APPS.has(appId)) return true;
		return appsLoaded && enabledApps[appId] === true;
	};

	// role_checklist drives which role groups may run each tour. '*' = everyone;
	// otherwise the user needs one of the listed role groups. No entries -> hidden
	// (fail closed — a tour must be explicitly assigned).
	const isRoleAllowed = (tour: TourChecklistItem): boolean => {
		const allowed = tour.allowedRoles ?? [];
		if (allowed.includes('*')) return true;
		return allowed.some((r) => userRoleNames.has(r.toLowerCase()));
	};

	const isTourAllowed = (tour: TourChecklistItem): boolean =>
		isAppAllowed(tour) && isRoleAllowed(tour);

	// Stage the tour for confirmation — deliberately does NOT start it yet.
	const handleStart = (tour: TourChecklistItem) => {
		setPendingTour(tour);
		setConfirmOpen(true);
	};

	// Close the confirm dialog (No / backdrop). Keep pendingTour until the
	// transition finishes (onExited) so the text doesn't collapse while closing.
	const cancelStart = () => setConfirmOpen(false);

	// User confirmed — now actually launch (may navigate) and close the popup.
	const confirmStart = () => {
		if (pendingTour) startTour(pendingTour.tourKey);
		setConfirmOpen(false);
		onStartTour?.();
	};

	const toggleGroup = (app: string) =>
		setOpenGroups((prev) => ({ ...prev, [app]: !prev[app] }));

	// Split into root-level tours and per-app groups, preserving the backend's
	// (app, name)-sorted order. Tours for apps the instance isn't subscribed to
	// are dropped entirely.
	const visibleTours = tours.filter(isTourAllowed);
	// Some CANpark tours need at least one parking lot; only run the check when one
	// of those tours is actually offered here.
	const lotGateOffered = visibleTours.some((t) =>
		LOT_REQUIRED_TOURS.has(t.tourKey),
	);
	const { hasLot, checked: lotChecked } =
		useTicketingTourReady(lotGateOffered);
	const rootTours = visibleTours.filter((t) => ROOT_TOUR_KEYS.has(t.tourKey));
	const groupOrder: string[] = [];
	const groups: Record<string, TourChecklistItem[]> = {};
	visibleTours.forEach((t) => {
		if (ROOT_TOUR_KEYS.has(t.tourKey)) return;
		if (!groups[t.app]) {
			groups[t.app] = [];
			groupOrder.push(t.app);
		}
		groups[t.app].push(t);
	});
	// Apply the explicit intra-group order (stable: unlisted tours keep backend order).
	groupOrder.forEach((app) => {
		groups[app].sort((a, b) => tourRank(a) - tourRank(b));
	});

	const renderTourItem = (tour: TourChecklistItem, nested = false) => {
		const meta = statusMeta(tour.status);
		const done = tour.status === 'completed';
		const blockedOnMobile =
			isBelowMd && tour.tourKey !== MOBILE_SAFE_TOUR_KEY;
		// Lot-dependent tours can't run without a parking lot: disable until we've
		// confirmed one exists (and once confirmed absent, show why).
		const needsLot = LOT_REQUIRED_TOURS.has(tour.tourKey);
		const noLot = needsLot && !hasLot;
		const noLotConfirmed = needsLot && lotChecked && !hasLot;
		const secondary = noLotConfirmed
			? 'Add a parking site first'
			: nested
				? meta.label
				: `${tour.app} · ${meta.label}`;
		return (
			<ListItem
				key={tour.tourKey}
				disableGutters
				sx={nested ? { pl: 2 } : undefined}
				secondaryAction={
					<Tooltip
						arrow
						placement="left"
						// Only the no-lot case gets a hover hint. A disabled MUI
						// Button doesn't emit hover events, so wrap it in a span.
						title={
							noLotConfirmed
								? 'Add a parking lot to run this tour. Need help setting one up? Try the “Manage Sites” walkthrough first.'
								: ''
						}
					>
						<span>
							<Button
								size="small"
								variant="outlined"
								disabled={
									!isTourRegistered(tour.tourKey) ||
									blockedOnMobile ||
									noLot
								}
								onClick={() => handleStart(tour)}
							>
								{done ? 'Replay' : 'Start'}
							</Button>
						</span>
					</Tooltip>
				}
			>
				<ListItemIcon sx={{ minWidth: 36 }}>{meta.icon}</ListItemIcon>
				<ListItemText
					primary={tour.tourName}
					secondary={secondary}
					primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
					secondaryTypographyProps={{ variant: 'caption' }}
				/>
			</ListItem>
		);
	};

	return (
		<>
		<Box sx={{ width: 340, maxWidth: '90vw', p: 2 }}>
			<Typography variant="subtitle1" fontWeight={600}>
				Guided tours
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				Guided tours to help you get started.
			</Typography>
			<Divider sx={{ mb: 1 }} />

			{isBelowMd && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						p: 1,
						mb: 1,
						borderRadius: 1,
						bgcolor: 'action.hover',
					}}
				>
					<LaptopIcon fontSize="small" color="action" />
					<Typography variant="caption" color="text.secondary">
						Most guided tours need a laptop or larger screen to run.
					</Typography>
				</Box>
			)}

			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
					<CircularProgress size={24} />
				</Box>
			) : error ? (
				<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
					Guided tours are unavailable right now.
				</Typography>
			) : visibleTours.length === 0 ? (
				<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
					No guided tours available.
				</Typography>
			) : (
				<List dense disablePadding>
					{rootTours.map((tour) => renderTourItem(tour))}

					{groupOrder.map((app) => {
						const open = !!openGroups[app];
						return (
							<React.Fragment key={app}>
								<ListItemButton
									onClick={() => toggleGroup(app)}
									sx={{ borderRadius: 1 }}
								>
									<ListItemText
										primary={app}
										primaryTypographyProps={{
											variant: 'body2',
											fontWeight: 600,
										}}
									/>
									{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								</ListItemButton>
								<Collapse in={open} timeout="auto" unmountOnExit>
									<List dense disablePadding>
										{groups[app].map((tour) =>
											renderTourItem(tour, true),
										)}
									</List>
								</Collapse>
							</React.Fragment>
						);
					})}
				</List>
			)}
		</Box>

		<Dialog
			open={confirmOpen}
			onClose={cancelStart}
			TransitionProps={{ onExited: () => setPendingTour(null) }}
		>
			<DialogTitle>Start guided tour</DialogTitle>
			<DialogContent>
				<Typography>
					Do you want to start
					{pendingTour ? ` the “${pendingTour.tourName}”` : ' this'}{' '}
					tour?
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={cancelStart} variant="outlined">
					No
				</Button>
				<Button onClick={confirmStart} variant="contained">
					Yes
				</Button>
			</DialogActions>
		</Dialog>
		</>
	);
};

export default OnboardingChecklist;
